import { useSetAtom } from 'jotai'
import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog as AriaDialog,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineShoppingCartCheckout from '~icons/ic/baseline-shopping-cart-checkout'
import IcRoundLock from '~icons/ic/round-lock'
import IcTwotoneError from '~icons/ic/twotone-error'
import MdiLoading from '~icons/mdi/loading'
import { UserCancelled } from 'src/components/Modal/Confirm'
import PaymentDialogV2 from 'src/components/Payment/PaymentDialogV2/PaymentDialogV2'
import {
  PAYMENT_PROVIDERS,
  PaymentMethodSelectItem,
} from 'src/components/Payment/PaymentMethodSelectItem'
import { PaymentDialogContentGmoCVS } from 'src/components/Payment/providers/GmoCvs'
import { PaymentDialogContentOnerway } from 'src/components/Payment/providers/onerway'
import { PaymentDialogContentOnerwayGoogle } from 'src/components/Payment/providers/OnerwayGooglePay'
import { PaymentDialogContentOnerwayWechatPay } from 'src/components/Payment/providers/OnerwayWechatPay'
import { PaymentDialogContentPayPay } from 'src/components/Payment/providers/paypay'
import { usePaymentProvider } from 'src/components/Payment/usePaymentProvider'
import { usePaymentMethodPageExperiment } from 'src/experiments/experiments/2605/paymentMethodPage'
import type { Currency } from 'src/services/apollo/generated/graphql'
import {
  getCurrentPrice,
  isPaymentDialogOpenAtom,
  type OpenPaymentDialogReturns,
  type PaymentExecuteFn,
  type PaymentOpts,
  PaymentProvider,
  type TrackingOpts,
} from 'src/services/helpers/payment'
import { createIntentToPayExecutor } from 'src/services/helpers/paymentExecutors'
import { formatPrice } from 'src/utils/currency'
import { cx } from 'src/utils/cx'
import { dynamicCreateElement } from 'src/utils/dynamic'
import { CreditsIcon } from '../Membership/MembershipIcon'
import { UIDialog } from '../ui/Modal'
import Text from '../ui/Text'

export type PaymentDialogHandlers = {
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
  onJcbAeUnionPayRequired?: () => void
}

export type PaymentDialogProps = PaymentOpts &
  TrackingOpts &
  PaymentDialogHandlers

const NoAvailableProviderContent = () => {
  const { t } = useTranslation(['membership'])
  return (
    <div className="px-4 py-8 flex flex-col items-start gap-1 border border-white text-white rounded-lg">
      <IcTwotoneError className="w-12 h-12 mb-2 opacity-70" />

      <h2 className="text-lg font-semibold">
        {t('membership:payment.no-available-providers.title')}
      </h2>
      <p className="text-sm">
        {t('membership:payment.no-available-providers.description')}
      </p>
    </div>
  )
}

const PaymentProviderSelectMenu: FC<{
  eligibleProviders: {
    provider: PaymentProvider
    preferred: boolean
  }[]
  selectedPaymentProvider: PaymentProvider | null
  onSelect: (provider: PaymentProvider) => void
}> = ({ eligibleProviders, onSelect, selectedPaymentProvider }) => {
  return (
    <div className="flex flex-col">
      <div className="grid gap-2 sm:grid-cols-3">
        {eligibleProviders.map(({ provider, preferred }) => {
          const config = PAYMENT_PROVIDERS.find(p => p.id === provider)
          const badge = config?.new ? (
            <Text i18nKey="common:new.label" />
          ) : preferred ? (
            <Text i18nKey="membership:payment.recommended" />
          ) : undefined

          return (
            <PaymentMethodSelectItem
              key={provider}
              id={provider}
              selected={selectedPaymentProvider === provider}
              badge={badge}
              onClick={() => onSelect(provider)}
            />
          )
        })}
      </div>
    </div>
  )
}

const UnionPayCreditCardOnlyNotice: FC = () => (
  <div className="mt-2 rounded-md border border-white/10 bg-white/10 px-3 py-2 text-xs leading-snug text-neutral-100">
    <Text i18nKey="membership:payment.unionpay-credit-card-only-notice" />
  </div>
)

const SelectedPaymentProviderContent: FC<
  {
    execute: PaymentExecuteFn
    selectedPaymentProvider: PaymentProvider | null
    trackingFnAfterFetch?: () => void
  } & PaymentDialogHandlers
> = ({
  execute,
  selectedPaymentProvider,
  onSucceeded,
  onFailed,
  onJcbAeUnionPayRequired,
  trackingFnAfterFetch,
}) => {
  if (!selectedPaymentProvider) {
    return null
  }

  return (
    <div className="grid gap-3">
      {selectedPaymentProvider === PaymentProvider.onerway && (
        <PaymentDialogContentOnerway
          key={PaymentProvider.onerway}
          execute={execute}
          trackingFnAfterFetch={trackingFnAfterFetch}
          onSucceeded={onSucceeded}
          onFailed={onFailed}
          onJcbAeUnionPayRequired={onJcbAeUnionPayRequired}
        />
      )}

      {selectedPaymentProvider === PaymentProvider.onerwayJcbAeUnionPay && (
        <PaymentDialogContentOnerway
          key={PaymentProvider.onerwayJcbAeUnionPay}
          execute={execute}
          executeProvider={PaymentProvider.onerwayJcbAeUnionPay}
          trackingFnAfterFetch={trackingFnAfterFetch}
          onSucceeded={onSucceeded}
          onFailed={onFailed}
        />
      )}

      {selectedPaymentProvider === PaymentProvider.onerwayGooglePay && (
        <PaymentDialogContentOnerwayGoogle
          execute={execute}
          trackingFnAfterFetch={trackingFnAfterFetch}
          onSucceeded={onSucceeded}
          onFailed={onFailed}
        />
      )}

      {selectedPaymentProvider === PaymentProvider.onerwayWechatPay && (
        <PaymentDialogContentOnerwayWechatPay
          execute={execute}
          trackingFnAfterFetch={trackingFnAfterFetch}
          onSucceeded={onSucceeded}
          onFailed={onFailed}
        />
      )}

      {selectedPaymentProvider === PaymentProvider.paypay && (
        <PaymentDialogContentPayPay
          execute={execute}
          trackingFnAfterFetch={trackingFnAfterFetch}
          onSucceeded={onSucceeded}
          onFailed={onFailed}
        />
      )}

      {selectedPaymentProvider === PaymentProvider.gmo && (
        <PaymentDialogContentGmoCVS
          execute={execute}
          trackingFnAfterFetch={trackingFnAfterFetch}
          onSucceeded={onSucceeded}
          onFailed={onFailed}
        />
      )}
    </div>
  )
}

const PaymentDialogContent: FC<
  {
    execute: PaymentExecuteFn
    loading: boolean
    eligibleProviders: {
      provider: PaymentProvider
      preferred: boolean
    }[]
    setSelectedPaymentProvider: (provider: PaymentProvider | null) => void
    selectedPaymentProvider: PaymentProvider | null
    scrollContainerRef?: React.RefObject<HTMLDivElement | null>
    trackingFnAfterFetch?: () => void
  } & PaymentDialogHandlers
> = ({
  execute,
  loading,
  eligibleProviders,
  selectedPaymentProvider,
  setSelectedPaymentProvider,
  scrollContainerRef,
  trackingFnAfterFetch,
  onSucceeded,
  onFailed,
  onJcbAeUnionPayRequired,
}) => {
  const selectedContentRef = useRef<HTMLDivElement>(null)

  // Scroll to SelectedPaymentProviderContent start when size changes
  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current
    const selectedContent = selectedContentRef.current

    if (!scrollContainer || !selectedContent) return

    const resizeObserver = new ResizeObserver(() => {
      // Scroll to start of SelectedPaymentProviderContent
      const containerRect = scrollContainer.getBoundingClientRect()
      const contentRect = selectedContent.getBoundingClientRect()

      // Calculate offset from container top
      const offsetTop =
        contentRect.top - containerRect.top + scrollContainer.scrollTop

      scrollContainer.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      })
    })

    resizeObserver.observe(selectedContent)

    return () => {
      resizeObserver.disconnect()
    }
  }, [scrollContainerRef, selectedPaymentProvider])

  if (loading)
    return (
      <MdiLoading className="w-8 h-8 animate-spin my-16 mx-auto text-white" />
    )

  if (eligibleProviders.length === 0) {
    return <NoAvailableProviderContent />
  }

  return (
    <>
      {eligibleProviders.length > 1 && (
        <PaymentProviderSelectMenu
          selectedPaymentProvider={selectedPaymentProvider}
          eligibleProviders={eligibleProviders}
          onSelect={setSelectedPaymentProvider}
        />
      )}
      {selectedPaymentProvider === PaymentProvider.onerwayJcbAeUnionPay && (
        <UnionPayCreditCardOnlyNotice />
      )}
      <div
        key={selectedPaymentProvider}
        ref={selectedContentRef}
        className="mt-2"
      >
        <SelectedPaymentProviderContent
          execute={execute}
          trackingFnAfterFetch={trackingFnAfterFetch}
          selectedPaymentProvider={selectedPaymentProvider}
          onSucceeded={onSucceeded}
          onFailed={onFailed}
          onJcbAeUnionPayRequired={onJcbAeUnionPayRequired}
        />
      </div>
    </>
  )
}

const PaymentDialog: FC<PaymentDialogProps> = ({
  onSucceeded,
  onFailed,
  ...props
}) => {
  const { isEnabled: isPaymentMethodPageV2 } = usePaymentMethodPageExperiment()

  if (isPaymentMethodPageV2) {
    return (
      <PaymentDialogV2
        {...props}
        onSucceeded={onSucceeded}
        onFailed={onFailed}
      />
    )
  }

  return (
    <PaymentDialogControl
      {...props}
      onSucceeded={onSucceeded}
      onFailed={onFailed}
    />
  )
}

const PaymentDialogControl: FC<PaymentDialogProps> = ({
  onSucceeded,
  onFailed,
  ...props
}) => {
  const [open, setOpen] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const setIsPaymentDialogOpen = useSetAtom(isPaymentDialogOpenAtom)

  // Track dialog open state for ForceRefreshChecker
  useEffect(() => {
    setIsPaymentDialogOpen(true)
    return () => setIsPaymentDialogOpen(false)
  }, [setIsPaymentDialogOpen])

  const { t } = useTranslation(['membership'])

  const executeFn: PaymentExecuteFn = useMemo(
    () =>
      props.executePayment ??
      createIntentToPayExecutor({
        itemId: props.itemId,
        oneTimeMembershipId: props.oneTimeMembership?.id,
        interval: props.interval,
        currency: props.currency,
        shouldDiscount: props.shouldDiscount,
        redirect: props.redirect,
        promotionOption: props.promotionOption,
      }),
    [
      props.executePayment,
      props.itemId,
      props.oneTimeMembership?.id,
      props.interval,
      props.currency,
      props.shouldDiscount,
      props.redirect,
      props.promotionOption,
    ],
  )

  const {
    loading,
    selectedPaymentProvider,
    setSelectedPaymentProvider,
    eligibleProviders,
  } = usePaymentProvider(props)

  const currentPrice = getCurrentPrice({
    selectedPaymentProvider,
    prices: props.prices,
    currency: props.currency,
  })

  const cancel = () => {
    setOpen(false)
    setTimeout(() => {
      onFailed?.(UserCancelled)
    }, 150) // wait for the dialog to animate out completely
  }

  const handleJcbAeUnionPayRequired = () => {
    setOpen(false)
    setTimeout(() => {
      props.onJcbAeUnionPayRequired?.()
    }, 150)
  }

  return (
    <AriaModalOverlay
      isOpen={open}
      onOpenChange={open => {
        if (!open) {
          cancel()
        }
      }}
      isKeyboardDismissDisabled
      className={cx(
        'fixed inset-0 z-[1400] bg-black/90 backdrop-blur-md select-none',
        // Animation
        '!duration-150',
        // Open animation (fade in overlay)
        'entering:animate-in',
        'entering:fade-in',
        // Close animation
        'exiting:animate-out',
        'exiting:fade-out',
      )}
    >
      <AriaModal
        className={cx(
          // place at center
          // Wide enough for three desktop payment methods; viewport max keeps narrow screens from overflowing.
          'fixed inset-0 z-[1400] w-[300px] max-w-[calc(100vw-32px)] pt-[10%] sm:w-[600px] sm:max-w-[calc(100vw-48px)] mx-auto',
          // Open animation (slide in dialog content)
          'entering:animate-in',
          'entering:slide-in-from-bottom-4',
          'entering:fade-in',
          'entering:duration-300',
          // Close animation
          'exiting:animate-out',
          'exiting:slide-out-to-bottom-4',
          'exiting:fade-out',
          'exiting:duration-150',
        )}
      >
        <AriaDialog className="outline-none flex flex-col h-full max-h-[90vh]">
          <div className="flex text-white items-center text-sm select-none mb-2 shrink-0">
            <span className="opacity-80">
              <IcBaselineShoppingCartCheckout className="w-5 h-5" />
            </span>
            {currentPrice && (
              <div className="font-bold tabular-nums text-sm opacity-80 tracking-wide px-2 leading-6 rounded-lg select-none">
                {formatPrice(currentPrice.currency, currentPrice.amount)}
              </div>
            )}
            {props.credits != null && props.credits > 0 && (
              <>
                <span className="opacity-50 mx-1">→</span>
                <div className="flex items-center gap-1 font-bold tabular-nums text-sm opacity-80 tracking-wide leading-6 select-none">
                  <CreditsIcon className="size-4" />
                  <span>{props.credits.toLocaleString()}</span>
                </div>
              </>
            )}

            <div className="flex-1" />

            <button
              onClick={() => {
                cancel()
              }}
              className="text-white bg-zinc-600 disabled:bg-black/50 ring-1 ring-black/20 p-1 hover:brightness-90 active:brightness-75 rounded-full"
            >
              <IcBaselineClose className="w-4 h-4" />
            </button>
          </div>

          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto min-h-0"
          >
            <PaymentDialogContent
              execute={executeFn}
              loading={loading}
              eligibleProviders={eligibleProviders}
              setSelectedPaymentProvider={setSelectedPaymentProvider}
              selectedPaymentProvider={selectedPaymentProvider}
              scrollContainerRef={scrollContainerRef}
              trackingFnAfterFetch={props.trackingFnAfterFetch}
              onSucceeded={onSucceeded}
              onFailed={onFailed}
              onJcbAeUnionPayRequired={handleJcbAeUnionPayRequired}
            />
          </div>

          <div className="flex items-start justify-center gap-2 mt-4 opacity-60 text-white text-sm select-none shrink-0">
            <IcRoundLock className="size-4 shrink-0" />

            <div className="flex flex-col gap-2 items-start">
              <h2 className="font-bold leading-4">
                {t('membership:plans-page.promotion.secure-payments.title')}
              </h2>
              <p className="text-neutral-200">
                {t(
                  'membership:plans-page.promotion.secure-payments.description',
                )}
              </p>
            </div>
          </div>
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  )
}

async function openOnerwayJcbAeUnionPayRequiredDialog(): Promise<boolean> {
  return UIDialog.confirm({
    className: 'max-w-[420px]',
    heading: (
      <Text i18nKey="membership:payment.onerway-card-group-retry.title" />
    ),
    content: (
      <Text i18nKey="membership:payment.onerway-card-group-retry.description" />
    ),
    cancelProps: {
      children: (
        <Text i18nKey="membership:payment.onerway-card-group-retry.cancel" />
      ),
    },
    confirmProps: {
      children: (
        <Text i18nKey="membership:payment.onerway-card-group-retry.confirm" />
      ),
    },
  })
}

/**
 * Initiate a payment.
 * Throws `UserCancelled` if the user cancels the payment (for popup providers only).
 * @returns Promise<void>
 */
export async function openPaymentDialog(
  props: PaymentOpts & TrackingOpts,
): Promise<OpenPaymentDialogReturns> {
  return new Promise<OpenPaymentDialogReturns>((resolve, reject) => {
    const reopenWithJcbAeUnionPay = () => {
      dismiss()
      void openOnerwayJcbAeUnionPayRequiredDialog().then(async confirmed => {
        if (!confirmed) {
          reject(UserCancelled)
          return
        }

        try {
          resolve(
            await openPaymentDialog({
              ...props,
              initialPaymentProvider: PaymentProvider.onerwayJcbAeUnionPay,
            }),
          )
        } catch (err) {
          reject(err)
        }
      })
    }

    const dismiss = dynamicCreateElement(
      <PaymentDialog
        {...props}
        onSucceeded={r => {
          resolve(r)
          dismiss()
        }}
        onFailed={err => {
          reject(err)
          dismiss()
        }}
        onJcbAeUnionPayRequired={reopenWithJcbAeUnionPay}
      />,
      'payment-dialog',
    )
  })
}
